import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiCheck,
  FiToggleLeft, FiToggleRight, FiCreditCard, FiAlertCircle,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

/* ── Constants ─────────────────────────────────────────────────── */

const ACCOUNT_TYPES = [
  { value: 'bank',   label: 'Bank Account' },
  { value: 'cash',   label: 'Cash'         },
  { value: 'upi',    label: 'UPI'          },
  { value: 'wallet', label: 'Wallet'       },
];

const TYPE_BADGE = {
  bank:   { bg: '#dbeafe', color: '#1d4ed8', label: 'Bank'   },
  cash:   { bg: '#dcfce7', color: '#16a34a', label: 'Cash'   },
  upi:    { bg: '#ede9fe', color: '#7c3aed', label: 'UPI'    },
  wallet: { bg: '#fef3c7', color: '#d97706', label: 'Wallet' },
};

const EMPTY_FORM = {
  account_name: '',
  account_type: 'bank',
  bank_name: '',
  account_number: '',
  ifsc_code: '',
  branch_name: '',
  upi_id: '',
  opening_balance: '',
  opening_balance_date: '',
  is_primary: false,
  notes: '',
};

/* ── Styles ────────────────────────────────────────────────────── */

const s = {
  page:       { padding: '28px 24px', maxWidth: 900, margin: '0 auto' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 },
  title:      { fontWeight: 700, fontSize: '1.4rem', margin: 0 },
  subtitle:   { color: '#6b7280', fontSize: 13, marginTop: 4 },
  card:       { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' },
  tableHead:  { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  th:         { padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#6b7280', textAlign: 'left', whiteSpace: 'nowrap' },
  td:         { padding: '14px 16px', fontSize: 14, color: '#111827', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
  badge:      (bg, color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color }),
  btn:        { padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnPrimary: { background: '#2563eb', color: '#fff' },
  btnDanger:  { background: '#fee2e2', color: '#dc2626' },
  btnGhost:   { background: '#f3f4f6', color: '#374151' },
  iconBtn:    { background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'inline-flex', alignItems: 'center' },
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:      { background: '#fff', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.15)' },
  label:      { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input:      { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  row2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  emptyState: { textAlign: 'center', padding: '56px 24px', color: '#9ca3af' },
};

/* ── Component ─────────────────────────────────────────────────── */

export default function AdminBankAccounts() {
  const [accounts, setAccounts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editAccount, setEditAccount] = useState(null); // null = add, object = edit
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  /* ── Fetch ── */
  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/company-bank-accounts');
      setAccounts(data.accounts || []);
    } catch (err) {
      toast.error('Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  /* ── Open modal ── */
  const openAdd = () => {
    setEditAccount(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (acc) => {
    setEditAccount(acc);
    setForm({
      account_name:         acc.account_name || '',
      account_type:         acc.account_type || 'bank',
      bank_name:            acc.bank_name || '',
      account_number:       acc.account_number || '',
      ifsc_code:            acc.ifsc_code || '',
      branch_name:          acc.branch_name || '',
      upi_id:               acc.upi_id || '',
      opening_balance:      acc.opening_balance !== undefined ? String(acc.opening_balance) : '',
      opening_balance_date: acc.opening_balance_date || '',
      is_primary:           !!acc.is_primary,
      notes:                acc.notes || '',
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditAccount(null); };

  /* ── Save ── */
  const handleSave = async () => {
    if (!form.account_name.trim()) return toast.error('Account name is required');
    if (!form.account_type)        return toast.error('Account type is required');

    try {
      setSaving(true);
      const payload = {
        ...form,
        opening_balance: form.opening_balance !== '' ? Number(form.opening_balance) : 0,
        ifsc_code: form.ifsc_code.toUpperCase(),
      };

      if (editAccount) {
        await API.put(`/company-bank-accounts/${editAccount.id}`, payload);
        toast.success('Account updated');
      } else {
        await API.post('/company-bank-accounts', payload);
        toast.success('Account added');
      }
      closeModal();
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /* ── Toggle active ── */
  const handleToggleActive = async (acc) => {
    try {
      await API.patch(`/company-bank-accounts/${acc.id}/toggle-active`);
      toast.success(acc.is_active ? 'Account deactivated' : 'Account activated');
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await API.delete(`/company-bank-accounts/${deleteTarget.id}`);
      toast.success('Account deleted');
      setDeleteTarget(null);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  /* ── Field helper ── */
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  /* ── Render ── */
  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Bank Accounts</h2>
          <p style={s.subtitle}>Manage your company's bank accounts, UPI IDs, and cash accounts used across payments.</p>
        </div>
        <button style={{ ...s.btn, ...s.btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }} onClick={openAdd}>
          <FiPlus size={14} /> Add Account
        </button>
      </div>

      {/* Table */}
      <div style={s.card}>
        {loading ? (
          <div style={s.emptyState}>Loading…</div>
        ) : accounts.length === 0 ? (
          <div style={s.emptyState}>
            <FiCreditCard size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No accounts yet</div>
            <div style={{ fontSize: 13 }}>Add your first bank account to start tracking payments by account.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={s.tableHead}>
              <tr>
                <th style={s.th}>Account</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Bank / UPI</th>
                <th style={s.th}>A/C No.</th>
                <th style={s.th}>Opening Bal.</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(acc => {
                const badge = TYPE_BADGE[acc.account_type] || TYPE_BADGE.bank;
                return (
                  <tr key={acc.id}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600 }}>{acc.account_name}</div>
                      {acc.is_primary && (
                        <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>★ Primary</span>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={s.badge(badge.bg, badge.color)}>{badge.label}</span>
                    </td>
                    <td style={s.td}>
                      <div>{acc.bank_name || acc.upi_id || '—'}</div>
                      {acc.branch_name && <div style={{ fontSize: 12, color: '#6b7280' }}>{acc.branch_name}</div>}
                    </td>
                    <td style={s.td}>
                      {acc.account_number_last4 ? `••••${acc.account_number_last4}` : (acc.upi_id ? '—' : '—')}
                    </td>
                    <td style={s.td}>
                      ₹{Number(acc.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={s.td}>
                      <span style={s.badge(
                        acc.is_active ? '#dcfce7' : '#f3f4f6',
                        acc.is_active ? '#16a34a' : '#6b7280',
                      )}>
                        {acc.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={{ ...s.iconBtn, color: '#2563eb' }} title="Edit" onClick={() => openEdit(acc)}>
                          <FiEdit2 size={15} />
                        </button>
                        <button
                          style={{ ...s.iconBtn, color: acc.is_active ? '#d97706' : '#16a34a' }}
                          title={acc.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => handleToggleActive(acc)}
                        >
                          {acc.is_active ? <FiToggleRight size={17} /> : <FiToggleLeft size={17} />}
                        </button>
                        <button style={{ ...s.iconBtn, color: '#dc2626' }} title="Delete" onClick={() => setDeleteTarget(acc)}>
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                {editAccount ? 'Edit Account' : 'Add Bank Account'}
              </h3>
              <button style={s.iconBtn} onClick={closeModal}><FiX size={18} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Account Name + Type */}
              <div style={s.row2}>
                <div>
                  <label style={s.label}>Account Name *</label>
                  <input style={s.input} value={form.account_name} onChange={set('account_name')} placeholder="e.g. HDFC Main Current" />
                </div>
                <div>
                  <label style={s.label}>Account Type *</label>
                  <select style={s.input} value={form.account_type} onChange={set('account_type')}>
                    {ACCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Bank-specific fields */}
              {form.account_type === 'bank' && (
                <>
                  <div style={s.row2}>
                    <div>
                      <label style={s.label}>Bank Name</label>
                      <input style={s.input} value={form.bank_name} onChange={set('bank_name')} placeholder="e.g. HDFC Bank" />
                    </div>
                    <div>
                      <label style={s.label}>Branch Name</label>
                      <input style={s.input} value={form.branch_name} onChange={set('branch_name')} placeholder="e.g. Ghaziabad Main" />
                    </div>
                  </div>
                  <div style={s.row2}>
                    <div>
                      <label style={s.label}>Account Number</label>
                      <input style={s.input} value={form.account_number} onChange={set('account_number')} placeholder="Full account number" />
                    </div>
                    <div>
                      <label style={s.label}>IFSC Code</label>
                      <input style={s.input} value={form.ifsc_code} onChange={set('ifsc_code')} placeholder="e.g. HDFC0001234" />
                    </div>
                  </div>
                </>
              )}

              {/* UPI field */}
              {form.account_type === 'upi' && (
                <div>
                  <label style={s.label}>UPI ID</label>
                  <input style={s.input} value={form.upi_id} onChange={set('upi_id')} placeholder="e.g. business@hdfcbank" />
                </div>
              )}

              {/* Opening Balance */}
              <div style={s.row2}>
                <div>
                  <label style={s.label}>Opening Balance (₹)</label>
                  <input style={s.input} type="number" min="0" value={form.opening_balance} onChange={set('opening_balance')} placeholder="0.00" />
                </div>
                <div>
                  <label style={s.label}>As of Date</label>
                  <input style={s.input} type="date" value={form.opening_balance_date} onChange={set('opening_balance_date')} />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={s.label}>Notes</label>
                <textarea style={{ ...s.input, resize: 'vertical', minHeight: 60 }} value={form.notes} onChange={set('notes')} placeholder="Optional notes about this account…" />
              </div>

              {/* Primary checkbox */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input type="checkbox" checked={form.is_primary} onChange={set('is_primary')} />
                Set as primary account (pre-selected in all payment forms)
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button style={{ ...s.btn, ...s.btnGhost }} onClick={closeModal}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary }} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editAccount ? 'Update Account' : 'Add Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth: 400 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <FiAlertCircle size={24} color="#dc2626" />
              <h3 style={{ margin: 0, fontWeight: 700 }}>Delete Account?</h3>
            </div>
            <p style={{ color: '#374151', fontSize: 14, marginBottom: 20 }}>
              Are you sure you want to delete <strong>{deleteTarget.account_name}</strong>?
              This cannot be undone. If the account is used in any payments, deletion will be blocked — deactivate it instead.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button style={{ ...s.btn, ...s.btnGhost }} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnDanger }} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
